import { ReactElement, useRef, useState } from 'react';
import { useUITranslation } from '../../hooks';
import styles from './ui-filter.module.scss';
import { UIPopover, UIPopoverPlacement, UIPropoverRef } from '../ui-popover';
import { UIMenu } from '../ui-menu';
import { UIButton } from '../ui-button';
import { UIDateRangePicker } from '../ui-date-range-picker';
import { UIFilterHeader } from './ui-filter-header';

type UIDateRangeFilterItemProps = {
    from: Date;
    to: Date;
    /**
     * The range as the viewer should read it. Passed in rather than formatted
     * here: the wording depends on their timezone and language, and neither is
     * something this library knows.
     */
    label: string;
    timezone: string;
    popoverPlacement?: UIPopoverPlacement;
    onDateChange: (from: Date, to: Date) => void;
};

export function UIDateRangeFilterItem(
    props: UIDateRangeFilterItemProps
): ReactElement {
    const { t } = useUITranslation();
    const { from, to, label, timezone, popoverPlacement, onDateChange } = props;

    const [internalFrom, setInternalFrom] = useState<Date>(from);
    const [internalTo, setInternalTo] = useState<Date>(to);

    const ref = useRef<UIPropoverRef>(null);

    function onClickOutside() {
        ref.current?.close();
    }

    function onApplyPress() {
        ref.current?.close();
        onDateChange(internalFrom, internalTo);
    }

    function onCancelPress() {
        ref.current?.close();
    }

    return (
        <UIPopover
            ref={ref}
            popoverPadding={8}
            width={280 * 2 + 24 * 2 + 1 + 16}
            triggerNode={
                <button className={styles.buttonContainer}>
                    <UIFilterHeader text={label} />
                </button>
            }
            placement={popoverPlacement}
            onClickOutside={onClickOutside}
        >
            <UIMenu
                scroll
                maxHeight={1000}
                maxWidth={1000}
                footer={
                    <div
                        style={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'row'
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'row',
                                justifyContent: 'flex-end',
                                gap: 4,
                                zIndex: 999 // Fixes bug where checkbox behind it is clicked instead
                            }}
                        >
                            <UIButton
                                variant="textDefault"
                                size="small"
                                onClick={onCancelPress}
                            >
                                {t('shared.labels.cancel')}
                            </UIButton>
                            <UIButton
                                variant="primary"
                                size="small"
                                onClick={onApplyPress}
                            >
                                {t('shared.labels.apply')}
                            </UIButton>
                        </div>
                    </div>
                }
            >
                <UIDateRangePicker
                    from={from}
                    to={to}
                    timezone={timezone}
                    onChange={(from, to) => {
                        setInternalFrom(from);
                        setInternalTo(to);
                    }}
                />
            </UIMenu>
        </UIPopover>
    );
}
